import { useContext } from 'react'

import { digObject } from '@domain-group/javascript-utils'

import {
  Button, ModalContext, SectionDivider, SidebarModal,
} from '@domain-group/suit-theme'

import type { SidebarModalProps } from '@domain-group/suit-theme'

import EntitySelector from '@components/EntitySelector'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import MemberListItem from './MemberListItem'
import useCreateBrandGroupMembersModal from './hooks/useCreateBrandGroupMembersModal'

interface ModalCallbacks {
  callbacks: {
    bulkCreateBrandGroupMembers: (payload: HandleCallbackActionParams) => Promise<object>,
  },
}

const CreateBrandGroupMembersModal = (props: SidebarModalProps & ModalCallbacks) => {
  const { callbacks, modalKey = 'CreateBrandGroupMembersModal', showModal } = props
  const { bulkCreateBrandGroupMembers, closeModal } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { brandGroup } = modalPayload

  const createBrandGroupMembersModalPayload = useCreateBrandGroupMembersModal(brandGroup)
  const {
    agencies,
    assignedMemberIds,
    callbacks: {
      bulkCreateBrandGroupMembers: createFn,
      toggleMembers,
    },
    creating,
    filteredBrandGroupMemberIds,
    hasAssignedMembers,
  } = createBrandGroupMembersModalPayload

  const bulkCreateBrandGroupMembersPayload = {
    callbacks: {
      action: createFn,
      afterAction: closeModal,
    },
    entityParams: { brandGroupId: brandGroup.id, memberIds: assignedMemberIds },
    requestOptions: { include_member_details: true },
    toastText: 'Members Assigned',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Add" titleSecondLine="Member" />

      <SidebarModal.Content>
        {/* @ts-expect-error */}
        <EntitySelector
          boxProps={{ marginTop: ['large', 0] }}
          callbacks={{
            selectItem: agency => toggleMembers(agency),
          }}
          selectedIds={[...filteredBrandGroupMemberIds, ...assignedMemberIds]}
          entityKey="agencies"
          searchRequestOptions={{ brand_id: brandGroup.brand_id }}
          showSelectedEntity={false}
          label="* Agency"
        />

        {hasAssignedMembers && (
          <>
            <SectionDivider marginTop="large">MEMBERS TO BE ADDED</SectionDivider>

            {assignedMemberIds.map((memberId) => {
              const agency = agencies[memberId] || {}

              return (
                <MemberListItem
                  agency={agency}
                  callbacks={{ toggleMembers: () => toggleMembers({ id: memberId }) }}
                  key={memberId}
                />
              )
            })}
          </>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!hasAssignedMembers}
          loading={creating}
          // @ts-expect-error TS2345
          onClick={() => bulkCreateBrandGroupMembers(bulkCreateBrandGroupMembersPayload)}
          size="large"
        >
          Save Members
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

// @ts-expect-error TS7006
const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <CreateBrandGroupMembersModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
